import awsRdsLogo from "core/assets/icons/integrations/aws-rds.svg";
import azurePostgresLogo from "core/assets/icons/integrations/azure-postgres.svg";
import databricksLogo from "core/assets/icons/integrations/databricks.svg";
import gcpCloudSqlLogo from "core/assets/icons/integrations/gcp-cloud-sql.svg";
import snowflakeLogo from "core/assets/icons/integrations/snowflake.svg";

export type DbProvider = {
  id: string;
  name: string;
  description: string;
  version: string;
};

export const providers: DbProvider[] = [
  {
    id: "aws-rds",
    name: "AWS Aurora Postgres",
    description: "Managed PostgreSQL on AWS Aurora",
    version: "v0.1.0",
  },
  {
    id: "azure-postgres",
    name: "Azure Database for PostgreSQL",
    description: "Managed PostgreSQL on Microsoft Azure",
    version: "v0.1.0",
  },
  {
    id: "databricks-lakebase",
    name: "Databricks Lakebase",
    description: "Postgres-compatible on the Databricks Lakehouse",
    version: "v0.1.0",
  },
  {
    id: "gcp-cloud-sql",
    name: "Cloud SQL for PostgreSQL",
    description: "Managed PostgreSQL on Google Cloud",
    version: "v0.1.0",
  },
  {
    id: "snowflake",
    name: "Snowflake Postgres",
    description: "Managed Postgres on Snowflake",
    version: "v0.1.0",
  },
];

export type ConnectionStatus =
  | "connected"
  | "degraded"
  | "not_connected"
  | "validating"
  | "validation_failed";

export type DataTagRow = {
  id: string;
  name: string;
  displayName: string;
  status: ConnectionStatus;
  activeDatabases: number | null;
  lastValidated: string | null;
};

export const initialDataTags: DataTagRow[] = [
  {
    id: "dev",
    name: "dev",
    displayName: "Development",
    status: "connected",
    activeDatabases: 14,
    lastValidated: "2m ago",
  },
  {
    id: "prod",
    name: "prod",
    displayName: "Production",
    status: "not_connected",
    activeDatabases: null,
    lastValidated: null,
  },
];

export type OpaAgent = {
  id: string;
  name: string;
  environment: string;
  host: string | null;
  lastSeen: string | null;
  status: ConnectionStatus;
  provider: string;
};

export const initialAgents: OpaAgent[] = [
  {
    id: "dev-agent",
    name: "Development",
    environment: "Development",
    host: "opa-dev.internal.acme.com",
    lastSeen: "2m ago",
    status: "connected",
    provider: "AWS Aurora Postgres",
  },
  {
    id: "prod-agent",
    name: "Production",
    environment: "Production",
    host: "opa-prod.internal.acme.com",
    lastSeen: "1m ago",
    status: "connected",
    provider: "AWS Aurora Postgres",
  },
];

export type ValidationCheck = {
  id: string;
  name: string;
  detail: string | null;
  status: "failed" | "passed" | "pending" | "running" | "skipped";
};

export const statusConfig: Record<
  ConnectionStatus,
  { label: string; dotClass: string; textClass: string }
> = {
  connected: {
    label: "Connected",
    dotClass: "bg-emerald-500",
    textClass: "text-emerald-700",
  },
  degraded: {
    label: "Degraded",
    dotClass: "bg-amber-500",
    textClass: "text-amber-700",
  },
  not_connected: {
    label: "Not connected",
    dotClass: "bg-gray-400",
    textClass: "text-gray-500",
  },
  validating: {
    label: "Validating",
    dotClass: "bg-blue-500 animate-pulse",
    textClass: "text-blue-700",
  },
  validation_failed: {
    label: "Validation failed",
    dotClass: "bg-red-500",
    textClass: "text-red-700",
  },
};

export function getValidationChecks(dataTag: string): ValidationCheck[] {
  const isFailing = dataTag === "staging";

  return [
    {
      id: "aws-reachable",
      name: "AWS account reachable from data plane",
      detail: null,
      status: "pending",
    },
    {
      id: "credential-resolver",
      name: "Credential resolver writable",
      detail: isFailing
        ? "AccessDeniedException: secretsmanager:CreateSecret denied. Add the permission from iam/permissions.json lines 23\u201331."
        : null,
      status: "pending",
    },
    {
      id: "iam-role",
      name: "IAM role assumable",
      detail: "arn:aws:iam::123456789012:role/superblocks-byo-db",
      status: "pending",
    },
    {
      id: "module-source",
      name: "Module source reachable",
      detail: "superblocksteam/byo-database-aws-rds v0.1.0",
      status: "pending",
    },
    {
      id: "network-rds",
      name: "Network reachability to RDS",
      detail: isFailing ? "Skipped \u2014 fix above first" : null,
      status: "pending",
    },
    {
      id: "provider-registry",
      name: "Provider registry reachable",
      detail: "hashicorp/aws ~> 5.0",
      status: "pending",
    },
    {
      id: "state-backend",
      name: "State backend reachable",
      detail: "s3://acme-superblocks-tf-state",
      status: "pending",
    },
    {
      id: "state-locking",
      name: "State backend locking available",
      detail: null,
      status: "pending",
    },
    {
      id: "tf-binary",
      name: "Terraform binary available on data plane",
      detail: "v1.6.2",
      status: "pending",
    },
  ];
}

export type ProviderId =
  | "aws-rds"
  | "azure-postgres"
  | "databricks-lakebase"
  | "gcp-cloud-sql"
  | "snowflake";

export type DataPlaneCopy = {
  accountNoun: string;
  cloneRepoUrl: string;
};

export const providerCopy: Record<ProviderId, DataPlaneCopy> = {
  "aws-rds": {
    accountNoun: "your AWS account",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-database-aws-rds-terraform",
  },
  "azure-postgres": {
    accountNoun: "your Azure subscription",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-database-azure-postgres-terraform",
  },
  "databricks-lakebase": {
    accountNoun: "your Databricks workspace",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-database-databricks-terraform",
  },
  "gcp-cloud-sql": {
    accountNoun: "your Google Cloud project",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-database-cloud-sql-terraform",
  },
  snowflake: {
    accountNoun: "your Snowflake account",
    cloneRepoUrl:
      "https://github.com/superblocksteam/byo-database-snowflake-terraform",
  },
};

export const providerLogos: Record<ProviderId, string> = {
  "aws-rds": awsRdsLogo,
  "azure-postgres": azurePostgresLogo,
  "databricks-lakebase": databricksLogo,
  "gcp-cloud-sql": gcpCloudSqlLogo,
  snowflake: snowflakeLogo,
};

export function getProviderIdFromName(name: string): ProviderId {
  const match = providers.find((p) => p.name === name);
  return (match?.id ?? "aws-rds") as ProviderId;
}

export function getYamlConfig(dataTag: string, providerId: ProviderId): string {
  switch (providerId) {
    case "azure-postgres":
      return `superblocks:
  lifecycleProfiles:
    - id: azure-postgres-${dataTag}
      type: database
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: azure_database_for_postgres
      isolation: shared
      module:
        source: superblocksteam/postgres-flexible-server-shared-slot/azurerm
        version: "~> 0.1"
      cloud:
        azure:
          subscriptionId: "<your-subscription-id>"
          tenantId: "<your-tenant-id>"
          resourceGroup: "<your-resource-group>"
          location: "<your-region>"
      state:
        backend: azurerm
        resourceGroupName: "<your-state-resource-group>"
        storageAccountName: "<your-state-storage-account>"
        containerName: superblocks-tf-state
        keyPrefix: superblocks/byo-db/${dataTag}
      credentialResolver:
        type: azure_key_vault
        vaultUri: "https://<your-vault>.vault.azure.net/"
        prefix: superblocks/${dataTag}/{application_id}/{database_requirement_id}
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
    case "databricks-lakebase":
      return `superblocks:
  lifecycleProfiles:
    - id: lakebase-${dataTag}
      type: database
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: databricks_lakebase_postgres
      isolation: shared
      module:
        source: superblocksteam/lakebase-postgres-shared-slot/databricks
        version: "~> 0.1"
      cloud:
        databricks:
          host: "https://<workspace-id>.cloud.databricks.com"
          warehouseId: "<sql-warehouse-id>"
          catalog: "<unity-catalog-name>"
      state:
        backend: databricks_volume
        volume: "/Volumes/<catalog>/<schema>/superblocks-state"
        keyPrefix: superblocks/byo-db/${dataTag}
      credentialResolver:
        type: databricks_secrets
        scope: superblocks-${dataTag}
        prefix: "{application_id}/{database_requirement_id}"
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
    case "gcp-cloud-sql":
      return `superblocks:
  lifecycleProfiles:
    - id: cloud-sql-${dataTag}
      type: database
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: gcp_cloud_sql_postgres
      isolation: shared
      module:
        source: superblocksteam/cloud-sql-postgres-shared-slot/google
        version: "~> 0.1"
      cloud:
        google:
          projectId: "<your-project-id>"
          region: "<your-region>"
          serviceAccount: "<your-service-account-email>"
      state:
        backend: gcs
        bucket: "<your-state-bucket>"
        keyPrefix: superblocks/byo-db/${dataTag}
      credentialResolver:
        type: gcp_secret_manager
        prefix: superblocks/${dataTag}/{application_id}/{database_requirement_id}
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
    case "snowflake":
      return `superblocks:
  lifecycleProfiles:
    - id: snowflake-${dataTag}
      type: database
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: snowflake_postgres
      isolation: shared
      module:
        source: superblocksteam/snowflake-postgres-shared-slot/snowflake
        version: "~> 0.1"
      cloud:
        snowflake:
          account: "<your-account-locator>"
          region: "<your-region>"
          role: "<your-role>"
          warehouse: "<your-warehouse>"
      state:
        backend: snowflake_stage
        stage: "@SUPERBLOCKS_DB.STATE.TF_STATE"
        keyPrefix: superblocks/byo-db/${dataTag}
      credentialResolver:
        type: snowflake_secrets
        database: SUPERBLOCKS_DB
        schema: SECRETS_${dataTag.toUpperCase()}
        prefix: "{application_id}/{database_requirement_id}"
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
    case "aws-rds":
    default:
      return `superblocks:
  lifecycleProfiles:
    - id: rds-${dataTag}
      type: database
      environmentClass: deployed
      environmentName: ${dataTag}
      provider: rds_aurora_postgres
      isolation: shared
      module:
        source: superblocksteam/rds-postgres-shared-slot/aws
        version: "~> 0.1"
      cloud:
        aws:
          accountId: "<your-account-id>"
          region: "<your-region>"
          assumeRoleArn: "<role-arn>"
      state:
        backend: s3
        bucket: "<your-state-bucket>"
        keyPrefix: superblocks/byo-db/${dataTag}
      credentialResolver:
        type: aws_secrets_manager
        prefix: superblocks/${dataTag}/{application_id}/{database_requirement_id}
      policies:
        autoApply: true
        deletionProtection: true
        softDeleteWindow: 14d`;
  }
}
